/**
 * PropertyViewContent.jsx — Contenu principal de la fiche Bien (consultation interne v5)
 * 
 * Référence mockup : /app/frontend/public/property_view_preview_v5.html
 * Conventions : /app/memory/DESIGN_CONVENTIONS.md (boutons monochromes, rounded-lg/2xl uniformes)
 * 
 * Structure :
 *   - Hero v5 (image + gradient + chips + titre)
 *   - KPIs dynamiques (4 cards selon opération)
 *   - TOC pills sticky
 *   - Grid 2/3 - 1/3 :
 *      • Main : Aperçu, Description, Capacité (CHR), Caractéristiques, Bail, Mandat, Performance, Confidentiel, Historique
 *      • Sidebar : Résumé IA, Négociateur, Localisation, Matching, Documents, Actions rapides
 *   - Photos (grid 4 cols)
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ContactMatchingWidget from './ContactMatchingWidget';
import LocationMap from './LocationMap';
import { isLicenceAsset, isPartsSocialesAsset } from '../../rules/propertyEditTabs';
import { formatPhoneFR, formatAdresse } from '../../utils/formatFR';
import { getMediaUrl } from '../../utils/mediaUrl';
import DocumentsInternesPanel, { formatSizeFR, formatDateFR } from '../documents/DocumentsInternesPanel';
import { deriveEquipements } from '../../utils/propertyDisplayFields';
import { ChiffresTableView, buildChiffresRows } from './ChiffresTableView';
import { renderDescriptionHtml } from '../../utils/sanitizeHtml';
import { getContactFullLabel } from '../../utils/contactIdentity';
import { buildPropertyKpis, getMetierLabel, KPI_GRID_CLASS } from '../../utils/propertyKpis';
import { getVille } from '../../utils/propertyDataMapper';
import { deriveHonorairesView, formatEuroFR, formatPercentFR, libellePrixTotal } from '../../utils/honorairesModel';
import ContextualSimulator from './ContextualSimulator';
import DiffusionCard from './DiffusionCard';
import DiffusionManagerModal from './DiffusionManagerModal';

const _API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const _token = () => localStorage.getItem('token') || '';
const MANDAT_TYPES = ['mandat_signe', 'avenant', 'bail_signe', 'compromis', 'promesse'];

// lot AM — barre « Communication & CRM » : mêmes classes que le bouton d'édition
// d'EditableBlock.jsx:81 (contour neutre au repos, bleu au survol). L'icône seule porte
// la couleur et ne change pas au survol ; le libellé reste gris.
const CRM_BTN = 'inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white '
  + 'text-slate-700 text-sm font-medium transition-colors duration-150 '
  + 'hover:border-blue-300 hover:bg-blue-50 hover:text-slate-900 '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007BFF] focus-visible:ring-offset-2';

// « Diffuser » : seule action qui publie à l'extérieur, en vert (couleur de « en ligne »).
const CRM_BTN_DIFFUSER = 'inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white '
  + 'text-slate-700 text-sm font-medium transition-colors duration-150 '
  + 'hover:border-emerald-200 hover:bg-emerald-50 hover:text-slate-900 '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#28A745] focus-visible:ring-offset-2';

/** Lien croisé (pas de duplication) : documents internes juridiques rattachés au bien,
 *  affichés à côté des champs du mandat. */
const LinkedMandatDocs = ({ propertyId }) => {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    if (!propertyId) return;
    let off = false;
    axios.get(`${_API}/documents`, { params: { entity_type: 'property', entity_id: propertyId } })
      .then((r) => { if (!off) setDocs((r.data || []).filter((d) => MANDAT_TYPES.includes(d.type_document))); })
      .catch(() => {});
    return () => { off = true; };
  }, [propertyId]);
  if (docs.length === 0) return null;
  return (
    <div className="mt-5 pt-4 border-t border-slate-100" data-testid="view-mandat-linked-docs">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Documents rattachés</p>
      <ul className="space-y-2">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
            <i className="bi bi-file-earmark-pdf text-rose-500" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{d.display_name}</p>
              <p className="text-[10px] text-slate-500">{formatSizeFR(d.size_bytes)} · {formatDateFR(d.uploaded_at)}</p>
            </div>
            <button onClick={() => { const tk = _token(); if (!tk) return; window.open(`${_API}/documents/${d.id}/download?auth=${encodeURIComponent(tk)}`, '_blank', 'noopener'); }}
              className="text-slate-400 hover:text-blue-600 p-1" title="Voir" data-testid={`view-mandat-doc-${d.id}`}><i className="bi bi-eye text-sm" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const fmtEuro = (n, opts = {}) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (Number.isNaN(v)) return null;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, ...opts }).format(v);
};

const fmtNumber = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (Number.isNaN(v)) return null;
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
};

const OPERATION_LABELS = {
  cession: 'Cession (PME/PMI & DAB)',
  vente_immo: 'Vente immobilière (murs)',
  vente_murs: 'Vente immobilière (murs)',  // Legacy alias
  vente_fonds: 'Cession (PME/PMI & DAB)',  // Legacy alias
  cession_bail: 'Cession de bail',  // Legacy
  location: 'Location pure',
  location_droit_entree: 'Location + pas-de-porte',
  location_gerance: 'Location-gérance',
  parts_sociales: 'Cession de parts sociales',
  licence: 'Licence de débit de boissons',
};

// Mapping codes d'emplacement (saisis en édition) → libellés lisibles
// La source de vérité est le dropdown ligne 1620 de PropertyEditPage.jsx
const EMPLACEMENT_LABELS = {
  n1: 'N°1 — meilleur emplacement',
  n1bis: 'N°1 bis — très bon emplacement',
  n2: 'N°2 — bon emplacement',
  hyper_centre: 'Hyper-centre',
  centre_ville: 'Centre-ville',
  quartier_affaires: "Quartier d'affaires",
  axe_passant: 'Axe passant',
  angle_rue: 'Angle de rue',
  avenue_principale: 'Avenue principale',
  rue_pietonne: 'Rue piétonne',
  galerie_cc: 'Galerie de centre commercial',
  zone_commerciale: 'Zone commerciale',
  zone_activite: "Zone d'activité",
  peripherie: 'Périphérie',
  parking_proximite: 'Parking à proximité',
};
const formatEmplacement = (code) => code ? (EMPLACEMENT_LABELS[code] || code) : null;

// Familles d'opérations : factorise les KPIs entre canonique et legacy
const isCessionFamily = (op) => ['cession', 'vente_fonds', 'cession_bail'].includes(op);
const isVenteImmoFamily = (op) => ['vente_immo', 'vente_murs'].includes(op);

// iter133 — détails d'exploitation masqués sur une vente de murs (on ne vend pas les murs
// d'un restaurant comme ceux d'un local vide, mais on ne vend pas non plus son quotidien).
const DETAIL_EXPLOITATION_HORS_MURS = [
  'Ancienneté activité', 'Ancienneté licence', 'Horaires',
  'Fermeture hebdomadaire', 'Congés annuels',
  "Normes d'hygiène / HACCP", 'Laboratoire', 'Sous franchise / réseau',
  "Type d'agrément", 'Redevance mensuelle',
  'Commission tabac /an', 'Commission presse /an', 'Commission Loto/FDJ /an',
  'Commission PMU /an',
];

const VISUAL_THEME_URL = {
  licence_iv: 'https://images.unsplash.com/photo-1572116469696-31de0f17cc34?auto=format&fit=crop&w=1600&q=80',
  licence_iii: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=1600&q=80',
  licence_restaurant: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80',
  licence_petite: 'https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=1600&q=80',
  licence_autre: 'https://images.unsplash.com/photo-1543007631-283050bb3e8c?auto=format&fit=crop&w=1600&q=80',
  ps_societe: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=1600&q=80',
  ps_holding: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=80',
  ps_finance: 'https://images.unsplash.com/photo-1460472178825-e5240623afd5?auto=format&fit=crop&w=1600&q=80',
};

// ════════ COMPOSANTS UI ════════

const SectionTitle = ({ children }) => (
  <h2 className="text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2.5" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}>
    <span className="w-1 h-5 rounded-sm bg-gradient-to-b from-blue-500 to-blue-700" aria-hidden="true" />
    {children}
  </h2>
);

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${className}`}>
    {children}
  </span>
);

/**
 * iter132 — État de publication d'un bloc, reflet direct de son interrupteur.
 * Un mot, pas de cadenas : « Publié » (vert discret) ou « Non publié » (gris).
 */
const PublicationState = ({ published, label, 'data-testid': testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
      published ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${published ? 'bg-emerald-500' : 'bg-slate-400'}`} />
    {label ? `${label} — ` : ''}{published ? 'Publié' : 'Non publié'}
  </span>
);


const TocPill = ({ href, icon, label, active, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    data-active={active || undefined}
    data-testid={`view-toc-pill-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition ${
      active
        ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    }`}
  >
    <i className={`bi ${icon}`} />
    {label}
  </a>
);

// ════════ PRINCIPAL ════════

const PropertyViewContent = ({
  property,
  negociateur,
  agence,
  canViewConfidential = true,
  onShowConfidential,
  onOpenDocument,
  // Callbacks barre CRM
  onEmail,
  onPhone,
  onTask,
  onCalendar,
  onAssign,
  onBroadcast,
  onPropertyPatched, // (property) => void — utilisé par DiffusionCard/Modal pour resync l'état parent
}) => {
  const [activeToc, setActiveToc] = useState('apercu');
  // Ref sur le strip d'onglets sticky : quand l'onglet actif change, on le
  // fait défiler automatiquement en vue (utile en mobile où le strip est
  // scrollable horizontalement).
  const tocScrollRef = useRef(null);
  useEffect(() => {
    const container = tocScrollRef.current;
    if (!container) return;
    const activeEl = container.querySelector('[data-active="true"]');
    if (activeEl && typeof activeEl.scrollIntoView === 'function') {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeToc]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [showDiffusionModal, setShowDiffusionModal] = useState(false);
  // Résumé IA (hooks déclarés avant tout return anticipé — règle des hooks React)
  const [aiSummaryOverride, setAiSummaryOverride] = useState(null);
  const [generatingAiSummary, setGeneratingAiSummary] = useState(false);

  // Scroll spy léger pour le TOC
  useEffect(() => {
    const ids = ['apercu', 'carac', 'bail', 'mandat', 'perf', 'fonds', 'confid', 'historique', 'photos'];
    const onScroll = () => {
      let current = 'apercu';
      for (const id of ids) {
        const el = document.getElementById(`sec-${id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < 200) current = id;
      }
      setActiveToc(current);
    };
    const scrollContainer = document.querySelector('.flex-1.overflow-auto') || window;
    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', onScroll);
  }, []);

  if (!property) return null;

  // ═══ Data extraction ═══
  // Taxonomy : peut être stockée comme objet imbriqué OU comme champs racine
  // (operation, profil, objet, sous_profil, cible). On reconstruit une vue unifiée.
  const taxonomy = (property.taxonomy && typeof property.taxonomy === 'object')
    ? property.taxonomy
    : {
        operation: property.operation,
        profil: property.profil,
        objet: property.objet,
        sous_profil: property.sous_profil,
        cible: property.cible,
      };
  const operation = taxonomy.operation || property.operation;
  const bg = property.bien_general || {};
  const comp = property.composition || {};
  const vente = property.informations_vente || {};
  const bail = property.informations_bail || {};
  const confid = property.informations_confidentielles || {};
  const honoraires = property.honoraires_calcul || {};
  const medias = property.medias && typeof property.medias === 'object' ? property.medias : {};
  const photos = (medias.photos || []).filter(p => p?.url);
  const documents = medias.documents || [];
  const visualTheme = medias.visuel_thematique;
  const publicLoc = property.public_location || {};
  const confidentialLoc = property.confidential_location || {};
  const reference = property.reference || (property.id ? property.id.slice(0, 8) : '');

  // Historique des prix (volet "Historique des prix")
  const priceHistory = Array.isArray(property.historique_prix) ? property.historique_prix : [];
  const _isLocationOp = ['location', 'location_gerance', 'location_droit_entree'].includes(operation);
  const currentRefPrice = _isLocationOp
    ? (property.informations_bail || {}).loyer_mensuel
    : ((property.informations_vente || {}).prix_cession ?? (property.informations_vente || {}).net_vendeur);
  const priceUnit = _isLocationOp ? ' €/mois' : ' €';
  const fmtPrice = (v) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    return (isNaN(n) ? v : n.toLocaleString('fr-FR')) + priceUnit;
  };

  // Description détaillée FR — priorité "Web" (fiche publique), fallback texte maître "Fiche Technique"
  const description_detaillee = (() => {
    const descs = property.descriptif?.descriptions;
    if (!descs || typeof descs !== 'object') return null;
    const getFr = (key) => {
      const lang_map = descs[key];
      if (!lang_map) return null;
      const fr = lang_map['Français'] || lang_map.fr || lang_map.FR;
      return fr?.content?.trim() ? fr.content : null;
    };
    const preferred = ['Web', 'web', 'Fiche Technique', 'fiche_technique'];
    for (const key of preferred) {
      const c = getFr(key);
      if (c) return c;
    }
    for (const type of Object.keys(descs)) {
      const c = getFr(type);
      if (c) return c;
    }
    return null;
  })();

  const title = bg.titre_annonce || `${OPERATION_LABELS[operation] || 'Bien'}${publicLoc.city_label ? ' — ' + publicLoc.city_label : ''}`;
  // iter128 — repli sur la ville de l'adresse confidentielle (ville seule) comme en public
  const ville = publicLoc.city_label || bg.ville_generale || getVille(property) || '—';
  const secteur = publicLoc.sector_label;
  const emplacementLabel = formatEmplacement(publicLoc.emplacement_code);
  const emplacementNote = publicLoc.emplacement_note;
  const surface = comp.superficie_totale ? `${fmtNumber(comp.superficie_totale)} m²` : null;

  // Hero image
  const mainPhoto = photos[0];
  const heroImage = mainPhoto?.url
    ? getMediaUrl(mainPhoto)
    : (visualTheme && VISUAL_THEME_URL[visualTheme])
    || 'https://images.unsplash.com/photo-1559329007-40df8a9345d8?auto=format&fit=crop&w=1600&q=80';

  // KPIs dynamiques selon opération
  // ⚠️ Les actifs incorporels (licence, parts sociales) sont testés EN PREMIER
  // car un bien peut avoir operation='location' MAIS profil='licence' (ex: location de licence).
  // Sans ça, il tomberait sur les KPIs location (avec "Surface") au lieu des KPIs licence.
  // Libellé métier (KPI + badge hero) : source de vérité unique = getActiviteMetier
  // Règle Thie (17/07/2026) : afficher UNIQUEMENT l'activité, JAMAIS le profil en préfixe.
  const metierLabel = getMetierLabel(property);

  // iter116 — KPI de tête : SOURCE UNIQUE partagée avec la fiche publique et le PDF
  // (`utils/propertyKpis.js`). Ne jamais recalculer un jeu de KPI ici.
  const kpis = buildPropertyKpis(property).filter((k) => !k.hidden);


  // Capacité CHR — le profil peut être stocké au root, dans bien_general, ou dans taxonomy.
  const _rawProfil = ((property.profil || bg.profil || taxonomy.profil) || '').toString().toLowerCase();
  const _sousProfil = (property.sous_profil || bg.sous_profil || taxonomy.sous_profil || '').toString().toLowerCase();
  const isCHR = _rawProfil === 'chr' || !!comp.nombre_couverts_total || !!comp.places_assises_interieur || !!comp.places_interieur;
  const isHotel = ['hotel', 'hotel_meuble'].includes(_sousProfil);
  const isLiberaux = _rawProfil === 'services_liberaux';
  const isTabac = _sousProfil === 'tabac_presse_pmu';
  const exp = property.exploitation || {};
  const _fmtInt = (v) => (v != null && !Number.isNaN(Number(v)) ? String(v) : null);
  // iter112 — Capacité d'accueil adaptative par profil.
  //   Vert (text-emerald-*) retiré : valeurs neutres = text-slate-900 tabular-nums.
  //   Vert réservé aux vrais états de succès (badges "Disponible", coche sauvegarde).
  //   Hôtel : Classement / Prix/nuitée / Occupation / Réception / Saisonnalité (Chambres est en KPI).
  //   Restaurant / brasserie : Couverts intérieur/terrasse + Places assises + Bar + Terrasse type + PMR.
  //   Libéraux : Salle d'attente / Salles consultation / Sanitaires / Normes médicales.
  //   Tabac : aucun champ (les commissions ont leur bloc dédié — lot1).
  let capaciteCHR = [];
  // iter113 — déduplication par SOURCE de donnée (et non par libellé) : « Couverts intérieur »
  // et « Places intérieur » lisent le même stockage → une donnée = un seul endroit.
  const _pickSrc = (keys) => {
    for (const k of keys) {
      const v = comp[k];
      if (v !== null && v !== undefined && v !== '') return { value: v, srcs: keys };
    }
    return { value: null, srcs: keys };
  };
  const _seatsInt = _pickSrc(['couverts_interieur', 'places_assises_interieur', 'places_interieur']);
  const _seatsTer = _pickSrc(['couverts_terrasse', 'places_assises_terrasse', 'places_terrasse']);
  if (isHotel) {
    capaciteCHR = [
      { label: 'Classement', value: exp.classement_hotel || null, strong: true },
      { label: 'Prix moy. /nuitée', value: comp.prix_moyen_nuitee ? `${fmtNumber(comp.prix_moyen_nuitee)} €` : null, strong: true, srcs: ['prix_moyen_nuitee'] },
      { label: "Taux d'occupation", value: exp.taux_occupation_moyen != null ? `${exp.taux_occupation_moyen}%` : null, strong: true },
      { label: 'Réception', value: comp.type_reception === 'Horaires définis' ? (comp.horaires_reception || 'Horaires définis') : (comp.type_reception || null), strong: true, srcs: ['type_reception', 'horaires_reception'] },
      { label: 'Saisonnalité', value: exp.saisonnalite || null, strong: true },
      { label: 'PMR', value: (comp.pmr || comp.pmr_accessible || bg.accessibilite_pmr) ? 'Oui' : null, strong: true },
    ].filter(c => c.value);
  } else if (isCHR) {
    capaciteCHR = [
      { label: 'Couverts intérieur', value: fmtNumber(_seatsInt.value || comp.nombre_couverts_total), strong: true, srcs: _seatsInt.value ? _seatsInt.srcs : [..._seatsInt.srcs, 'nombre_couverts_total'] },
      { label: 'Couverts terrasse', value: fmtNumber(_seatsTer.value), strong: true, srcs: _seatsTer.srcs },
      { label: 'Nombre de salles', value: fmtNumber(comp.nombre_salles), strong: true, srcs: ['nombre_salles'] },
      { label: 'Type de cuisine', value: comp.cuisine_type || comp.type_cuisine || null, strong: true },
      { label: 'Bar', value: comp.bar_existe ? 'Oui' : null, strong: true },
      { label: 'Terrasse', value: comp.terrasse_existe ? (comp.terrasse_type || 'Oui') : null, strong: true },
      { label: 'Extraction', value: (comp.extraction_existe || comp.extraction) ? 'Oui' : null, strong: true },
      { label: 'PMR', value: (comp.pmr || comp.pmr_accessible || bg.accessibilite_pmr) ? 'Oui' : null, strong: true },
    ].filter(c => c.value);
  } else if (isLiberaux) {
    // Cabinet médical / dentaire / libéraux : projection des champs spécifiques
    const isMedical = ['cabinet_medical', 'cabinet_dentaire', 'cabinet_paramedical', 'laboratoire_analyses'].includes(_sousProfil);
    capaciteCHR = [
      { label: 'Nombre de pièces', value: fmtNumber(comp.nb_pieces), strong: true },
      { label: 'Nombre de bureaux', value: fmtNumber(comp.nb_bureaux), strong: true },
      { label: "Salle d'attente", value: comp.salle_attente ? 'Oui' : null, strong: true },
      { label: 'Secrétariat', value: comp.secretariat_accueil ? 'Oui' : null, strong: true },
      { label: 'Sanitaires', value: fmtNumber(comp.nb_sanitaires), strong: true },
      ...(isMedical ? [
        { label: 'Salles de consultation', value: fmtNumber(comp.nb_salles_consultation), strong: true },
        { label: 'Salles de soins', value: fmtNumber(comp.nb_salles_soins), strong: true },
        { label: 'Fauteuils dentaires', value: fmtNumber(comp.nb_fauteuils_dentaires), strong: true },
        { label: 'Normes médicales', value: comp.normes_medicales ? 'Oui' : null, strong: true },
        { label: 'PMR obligatoire', value: comp.pmr_obligatoire ? 'Oui' : null, strong: true },
      ] : []),
      { label: 'Places parking', value: fmtNumber(comp.parking_places), strong: true, srcs: ['parking_places'] },
      { label: 'PMR', value: (comp.pmr || comp.pmr_accessible || bg.accessibilite_pmr) ? 'Oui' : null, strong: true },
    ].filter(c => c.value);
  } else if (isTabac) {
    // Tabac / Presse / PMU — lot1 : les commissions ont leur bloc dédié (un montant en euros
    // n'est pas une capacité d'accueil) ; agréments Douanes / transférabilité / statut du
    // buraliste et PMR retirés (décision client). Les valeurs restent en base.
    capaciteCHR = [];
  } else {
    // Fallback (retail, bureaux, industriel...) : capacité générique
    capaciteCHR = [
      { label: 'Postes de travail', value: fmtNumber(comp.postes_travail), strong: true, srcs: ['postes_travail'] },
      { label: 'Salles de réunion', value: fmtNumber(comp.salles_reunion_nombre || comp.nb_salles_reunion), strong: true, srcs: ['salles_reunion_nombre', 'nb_salles_reunion'] },
      { label: 'Places parking', value: fmtNumber(comp.parking_places), strong: true, srcs: ['parking_places'] },
      { label: 'Quais de chargement', value: fmtNumber(comp.quais_chargement), strong: true, srcs: ['quais_chargement'] },
      { label: 'PMR', value: (comp.pmr || comp.pmr_accessible || bg.accessibilite_pmr) ? 'Oui' : null, strong: true },
    ].filter(c => c.value);
  }

  // lot1 — Bloc « Commissions » : les 4 commissions annuelles du buraliste. La section était
  // annoncée en commentaire depuis iter113 sans jamais exister.
  const commissions = (isTabac && !isVenteImmoFamily(operation) ? [
    { label: 'Commission tabac /an', value: exp.commission_tabac_an },
    { label: 'Commission presse /an', value: exp.commission_presse_an },
    { label: 'Commission Loto/FDJ /an', value: exp.commission_loto_fdj_an },
    { label: 'Commission PMU /an', value: exp.commission_pmu_an },
  ] : []).filter(c => c.value != null).map(c => ({ ...c, value: `${fmtNumber(c.value)} €` }));

  // iter133 — vente de murs : mêmes retraits dans « Capacité d'accueil » (les commissions
  // d'un buraliste ou son agrément ne se vendent pas avec les murs).
  if (isVenteImmoFamily(operation)) {
    capaciteCHR = capaciteCHR.filter((c) => !DETAIL_EXPLOITATION_HORS_MURS.includes(c.label));
  }


  // iter113 — clés de données déjà consommées par « Capacité d'accueil » : elles ne
  // doivent PAS être re-rendues dans « Composition du bien ». Si capaciteCHR est vide,
  // rien n'est consommé → les capacités génériques restent affichées (aucune perte).
  const _consumedSrcs = new Set();
  capaciteCHR.forEach((c) => (c.srcs || []).forEach((k) => _consumedSrcs.add(k)));
  const _notConsumed = (item) => !(item.srcs || []).some((k) => _consumedSrcs.has(k));

  // Composition COMPLÈTE — toutes les surfaces selon disponibilité
  // Pour un CHR, "Surface vente" n'a pas de sens métier (utiliser "Surface salle" à la place).
  const surfaces = [
    { label: 'Surface totale', value: comp.superficie_totale, unit: 'm²', highlight: true },
    { label: 'Surface vente', value: isCHR ? null : comp.surface_vente, unit: 'm²' },
    { label: 'Surface salle', value: comp.surface_salle, unit: 'm²' },
    { label: 'Cuisine', value: comp.surface_cuisine, unit: 'm²' },
    { label: 'Surface bureaux', value: comp.bureaux_m2 || comp.surface_bureaux, unit: 'm²' },
    { label: 'Bureaux fermés', value: comp.surface_bureaux_fermes, unit: 'm²' },
    { label: 'Open space', value: comp.surface_open_space, unit: 'm²' },
    { label: 'Surface salles de réunion', value: comp.surface_salles_reunion, unit: 'm²' },
    { label: 'Accueil', value: comp.surface_accueil, unit: 'm²' },
    { label: 'Réserve', value: comp.reserve, unit: 'm²' },
    { label: 'Stockage', value: comp.surface_stockage, unit: 'm²' },
    { label: 'Production', value: comp.surface_production, unit: 'm²' },
    { label: 'Archives', value: comp.surface_archives, unit: 'm²' },
    { label: 'Mezzanine', value: comp.surface_mezzanine, unit: 'm²' },
    { label: 'Kitchenette', value: comp.surface_kitchenette, unit: 'm²' },
    { label: 'Terrasse', value: comp.terrasse_m2, unit: 'm²' },
    { label: 'Surface extérieure', value: comp.surface_exterieure, unit: 'm²' },
  ].filter(s => s.value);

  // Caractéristiques structurelles
  const _ouv = property.ouverture_fermeture || {};
  const _statutBien = (bg.occupe === true) ? 'Occupé' : (bg.occupe === false ? 'Libre' : (bg.statut_bien || null));
  const structurelles = [
    { label: 'Statut du bien', value: _statutBien },
    { label: 'Date de fin d\'occupation', value: bg.date_fin_occupation ? new Date(bg.date_fin_occupation).toLocaleDateString('fr-FR') : null },
    { label: 'Ancienneté activité', value: bg.anciennete_activite || null },
    { label: 'Ancienneté licence', value: bg.anciennete_licence || null },
    { label: 'Horaires', value: _ouv.horaires_ouverture_fermeture || null },
    // lot CX — deux champs saisis de longue date mais jamais rendus visibles.
    { label: 'Fermeture hebdomadaire', value: _ouv.fermeture_hebdomadaire || null },
    { label: 'Congés annuels', value: _ouv.conges_fermeture_saisonniere || null },
    { label: 'Appartement privé', value: comp.appartement_prive || null },
    { label: 'Type d\'emplacement', value: emplacementLabel },
    { label: 'Précision emplacement', value: emplacementNote },
    { label: 'Hauteur plafond', value: comp.hauteur_plafond ? `${comp.hauteur_plafond} m` : null },
    { label: 'Hauteur libre', value: comp.hauteur_libre ? `${comp.hauteur_libre} m` : null },
    { label: 'Linéaire de façade', value: comp.lineaire_facade ? `${comp.lineaire_facade} m` : null },
    { label: 'Façade', value: comp.facade ? `${comp.facade}${typeof comp.facade === 'number' ? ' m' : ''}` : null },
    { label: 'Étage', value: bg.etage || null },
    { label: 'Vitrine', value: comp.vitrine || null },
    // Conformité / accessibilité (les booléens PMR/terrasse sont dans Équipements — règle coche verte)
    { label: 'Classement ERP', value: bg.classement_erp || null },
    // lot A — état des travaux : donnée saisie depuis vingt ans côté CasaSoft (1 030 biens),
    // reprise par l'ETL et jamais affichée. INTERNE : c'est un argument de négociation.
    { label: 'Travaux à prévoir', value: bg.travaux_prevoir || null },
    // Localisation utile (saisis bien_general, jamais affichés avant)
    { label: 'Transports à proximité', value: bg.transports_proximite || null },
    { label: 'Parking à proximité', value: bg.parking_proximite || null },
    { label: 'Flux piéton', value: bg.flux_pieton || null },
    // Redevance terrasse — champ saisi mais oublié en affichage
    {
      label: 'Redevance terrasse',
      value: comp.redevance_terrasse_an
        ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(comp.redevance_terrasse_an))}\u00A0€/an`
        : null,
    },
    // iter107 — Hôtellerie & réception
    { label: 'Prix moyen /nuitée', value: comp.prix_moyen_nuitee ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(comp.prix_moyen_nuitee))}\u00A0€` : null, srcs: ['prix_moyen_nuitee'] },
    { label: 'Réception', value: comp.type_reception === 'Horaires définis' ? (comp.horaires_reception || 'Horaires définis') : (comp.type_reception || null), srcs: ['type_reception', 'horaires_reception'] },
    // iter118 — clés d'exploitation diffusées en public : mêmes libellés ici (une donnée, un libellé)
    { label: "Normes d'hygiène / HACCP", value: exp.hygiene_haccp || null },
    {
      label: 'Laboratoire',
      value: (exp.laboratoire_present === 'Oui' || exp.laboratoire_present === true)
        ? (exp.laboratoire_surface_m2 ? `Oui — ${exp.laboratoire_surface_m2} m²` : 'Oui')
        : null,
    },
    { label: 'Sous franchise / réseau', value: (exp.sous_franchise === 'Oui' || exp.sous_franchise === true) ? 'Oui' : null },
    { label: "Type d'agrément", value: exp.agrement_type || null },
    {
      label: 'Redevance mensuelle',
      value: exp.licence_redevance_mensuelle
        ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(exp.licence_redevance_mensuelle))}\u00A0€/mois`
        : null,
    },
  ]
    .filter(c => c.value)
    // iter133 — vente de murs : l'activité de l'occupant qualifie le bien, son QUOTIDIEN non.
    // Les valeurs restent en base, elles ne sont simplement pas l'objet de la vente.
    .filter(c => !(isVenteImmoFamily(operation) && DETAIL_EXPLOITATION_HORS_MURS.includes(c.label)))
    .filter(_notConsumed);

  // Capacités (chiffres) — source unique : tout ce qui est déjà rendu dans
  // « Capacité d'accueil » est exclu ici (dédup par clé de données, iter113).
  const capacitesNumber = [
    { label: 'Chambres (hôtel)', value: property.exploitation?.nombre_chambres },
    { label: 'Restaurants', value: comp.nombre_restaurants, srcs: ['nombre_restaurants'] },
    { label: 'Niveaux', value: comp.nombre_niveaux, srcs: ['nombre_niveaux'] },
    { label: 'Couverts (total)', value: comp.nombre_couverts_total, srcs: ['nombre_couverts_total'] },
    { label: isCHR ? 'Couverts intérieur' : 'Places intérieur', value: comp.places_interieur || comp.places_assises_interieur || comp.couverts_interieur, srcs: ['places_interieur', 'places_assises_interieur', 'couverts_interieur'] },
    { label: isCHR ? 'Couverts terrasse' : 'Places terrasse', value: comp.places_terrasse || comp.places_assises_terrasse || comp.couverts_terrasse, srcs: ['places_terrasse', 'places_assises_terrasse', 'couverts_terrasse'] },
    { label: 'Postes de travail', value: comp.postes_travail, srcs: ['postes_travail'] },
    { label: 'Salles de réunion', value: comp.salles_reunion_nombre || comp.nb_salles_reunion, srcs: ['salles_reunion_nombre', 'nb_salles_reunion'] },
    { label: 'Places parking', value: comp.parking_places, srcs: ['parking_places'] },
    { label: 'Quais de chargement', value: comp.quais_chargement, srcs: ['quais_chargement'] },
    { label: 'Salles', value: comp.nombre_salles && comp.nombre_salles > 0 ? comp.nombre_salles : null, srcs: ['nombre_salles'] },
    { label: 'Employés', value: comp.nombre_employes && comp.nombre_employes > 0 ? comp.nombre_employes : null },
  ].filter(c => c.value).filter(_notConsumed);

  // Équipements & options — util partagé (booléen → coche si vrai ; valeur → "Libellé : valeur")
  const equipementsRendered = deriveEquipements(property);

  // Bail visible ? Selon opération
  // Compat titre_locatif : string (legacy) OU objet TitreLocatif (cible)
  const titreLocatifIsString = typeof bail.titre_locatif === 'string';
  const titreLocatifType = titreLocatifIsString ? bail.titre_locatif : (bail.titre_locatif?.type || null);
  const titreLocatifDuree = !titreLocatifIsString && bail.titre_locatif?.duree;
  const titreLocatifDestination = !titreLocatifIsString && bail.titre_locatif?.destination;
  const titreLocatifIndexation = !titreLocatifIsString && bail.titre_locatif?.indexation;

  // ============================================================
  // DÉTECTION ÉTENDUE : licence d'exploitation = pas de section bail commercial
  // (complète la fonction importée isLicenceAsset(taxonomy) avec des heuristiques
  // sur titre_annonce et commune_rattachement)
  // ============================================================
  const _s = (v) => (v || '').toString().toLowerCase();
  const looksLikeLicence =
    isLicenceAsset(taxonomy)
    || operation === 'licence'
    || _s(bg.profil).includes('licence')
    || _s(bg.sous_profil).includes('licence')
    || _s(bg.objet).includes('licence')
    || _s(bg.titre_annonce).includes('licence')
    || (bg.commune_rattachement && !bg.adresse_libre && !(property.confidential_location || {}).street);
  const looksLikePartsSociales = operation === 'parts_sociales' || isPartsSocialesAsset(taxonomy);

  const showBail = !looksLikeLicence
    && !looksLikePartsSociales
    && ['cession', 'vente_fonds', 'cession_bail', 'location', 'location_droit_entree', 'location_gerance', 'vente_immo', 'vente_murs'].includes(operation)
    && (titreLocatifType || bail.loyer_mensuel || bail.loyer_annuel || bail.redevance_mensuelle || bail.droit_entree || bail.charges || bail.charges_mensuelles || bail.depot_garantie || bail.date_debut_bail || bail.date_echeance_bail || bail.duree_gerance || bail.loyer_soumis_tva || bail.impot_foncier != null);

  // Mandat
  const mandatNumero = property.numero_mandat || property.mandat?.numero_mandat;
  const mandatType = property.type_mandat || property.mandat?.type;
  const mandatDateSig = property.date_mandat || property.mandat?.date_signature;
  const mandatDateFin = property.date_fin_mandat || property.mandat?.date_fin;
  const showMandat = !!(mandatNumero || mandatType || mandatDateSig || mandatDateFin);

  // Performance financière (interne uniquement)
  // iter107 — source de vérité étendue : exploitation.chiffres.* (nouveau) + legacy confid.*
  const chiffres = (property.exploitation && property.exploitation.chiffres) || {};

  // lot2 — UN SEUL rendu du tableau, partagé avec la fiche publique (`ChiffresTableView`).
  // iter133 avait refondu le tableau ici seulement : la fiche publique était restée sur
  // l'empilement par année. Les replis legacy (`confid.*`) restent INTERNES.
  const perfRows = buildChiffresRows(chiffres, {
    ca_n1: confid.ca_n1,
    ca_n2: confid.ca_n2,
    ca_annuel: confid.chiffre_affaires_annuel,
    ebe: confid.ebe,
    resultat_net: confid.benefices_annuels,
  });

  const showPerf = canViewConfidential && perfRows.length > 0;

  // iter133 — les salariés et le stock quittent le bloc financier : ils rejoignent
  // « Exploitation — Le fonds », comme dans la saisie (iter132).
  const showFonds = canViewConfidential && (confid.effectif_salaries != null || confid.detail_salaries
    || confid.date_creation_etablissement != null || confid.valeur_stock != null);
  // iter132 — état de publication réel, piloté par les interrupteurs (remplace le badge
  // statique « Interne » qui ne pilotait rien et devenait faux dès qu'un bloc était public).
  const caPublie = chiffres.afficher_ca_public !== false;
  const effectifPublie = confid.afficher_effectif_public === true;


  // Négociateur
  const negoFullName = negociateur ? `${negociateur.prenom || ''} ${negociateur.nom || ''}`.trim() : null;
  const negoInitials = negociateur
    ? `${(negociateur.prenom || '').charAt(0)}${(negociateur.nom || '').charAt(0)}`.toUpperCase()
    : 'AG';

  // Résumé IA : utiliser le résumé stocké en DB si présent, sinon fallback texte calculé
  const resumeIA = aiSummaryOverride || property.ai_summary || (() => {
    const parts = [];
    if (OPERATION_LABELS[operation]) parts.push(`${OPERATION_LABELS[operation]} à ${ville}`);
    if (surface) parts.push(`${surface}`);
    const prix = fmtEuro(vente.prix_cession || vente.net_vendeur || bail.loyer_mensuel);
    if (prix) parts.push(`Prix : ${prix}`);
    if (bg.licence_restaurant) parts.push(/^licence/i.test(bg.licence_restaurant) ? bg.licence_restaurant : `Licence ${bg.licence_restaurant}`);
    return parts.length ? parts.join('. ') + '.' : 'Bien immobilier commercial.';
  })();

  const regenerateAiSummary = async () => {
    if (!property?.id) return;
    setGeneratingAiSummary(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/properties/${property.id}/generate-summary`,
        {},
        { headers }
      );
      if (resp.data?.summary) {
        setAiSummaryOverride(resp.data.summary);
        try { toast.success('Résumé IA régénéré et enregistré'); } catch (_) { /* noop */ }
      } else if (resp.data?.message) {
        try { toast.error(resp.data.message); } catch (_) { /* noop */ }
      }
    } catch (e) {
      console.warn('[AI summary] erreur génération', e);
      const msg = e?.response?.data?.detail || 'Impossible de régénérer le résumé';
      try { toast.error(msg); } catch (_) { /* noop */ }
    } finally {
      setGeneratingAiSummary(false);
    }
  };

  // Localisation Leaflet (URL de tile - simple iframe-like via OSM static)
  const lat = canViewConfidential && confidentialLoc.lat ? confidentialLoc.lat : (publicLoc.zone_lat || null);
  const lon = canViewConfidential && confidentialLoc.lon ? confidentialLoc.lon : (publicLoc.zone_lon || null);

  return (
    <div className="bg-slate-50" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="property-view-content-v5">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Outfit:wght@500;600;700;800;900&display=swap');`}</style>

      {/* ═════════ HERO ═════════ */}
      <section id="sec-apercu" className="relative scroll-mt-32" data-testid="view-hero">
        <div
          className="h-[300px] md:h-[380px] relative bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(135deg,rgba(11,18,32,.05),rgba(11,18,32,.55)),url('${heroImage}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
          <div className="relative max-w-6xl mx-auto px-4 md:px-8 h-full flex flex-col justify-end pb-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
      {bg.profil || metierLabel ? <Chip className="bg-white/10 text-white/90 ring-1 ring-white/20 backdrop-blur">{metierLabel || bg.profil}</Chip> : null}
              <Chip className="bg-white/10 text-white/90 ring-1 ring-white/20 backdrop-blur">{OPERATION_LABELS[operation] || 'Bien'}</Chip>
              {bg.licence_restaurant && <Chip className="bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/30">Licence {bg.licence_restaurant}</Chip>}
            </div>
            <h1 className="text-white font-bold text-2xl md:text-4xl leading-tight max-w-3xl break-words [overflow-wrap:anywhere]" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }} data-testid="view-title">
              {title}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-white/80 text-xs md:text-sm">
              <span className="inline-flex items-center gap-1.5"><i className="bi bi-geo-alt-fill" />{ville}{secteur ? ` — ${secteur}` : ''}</span>
              {emplacementLabel && (
                <span className="inline-flex items-center gap-1.5" data-testid="view-hero-emplacement"><i className="bi bi-pin-map-fill" />{emplacementLabel}{emplacementNote ? ` (${emplacementNote})` : ''}</span>
              )}
              {surface && <span className="inline-flex items-center gap-1.5"><i className="bi bi-rulers" />{surface}</span>}
              {comp.nombre_couverts_total && <span className="inline-flex items-center gap-1.5">{fmtNumber(comp.nombre_couverts_total)} couverts</span>}
              {reference && <span className="inline-flex items-center gap-1.5"><i className="bi bi-hash" />{reference}</span>}
            </div>
          </div>
        </div>
      </section>

      {/* ═════════ BARRE COMMUNICATION & CRM (lot AM : boutons lisibles comme cliquables) ═════════
          Motif repris d'EditableBlock.jsx:81 (border-slate-200 → hover:border-blue-300 +
          hover:bg-blue-50) : contour neutre au repos, l'icône seule porte la couleur. */}
      <section className="bg-white border-y border-slate-200 mt-0" data-testid="property-view-crm-bar">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-1.5">
          <button
            onClick={onEmail}
            data-testid="property-view-email-btn"
            className={CRM_BTN}
            title="Envoyer un email"
          >
            <i className="bi bi-envelope text-[#007BFF]" />
            <span className="hidden md:inline">Email</span>
          </button>
          <button
            onClick={onPhone}
            data-testid="property-view-phone-btn"
            className={CRM_BTN}
            title="Appeler un contact"
          >
            <i className="bi bi-telephone text-[#007BFF]" />
            <span className="hidden md:inline">Téléphone</span>
          </button>
          <button
            onClick={onTask}
            data-testid="property-view-task-btn"
            className={CRM_BTN}
            title="Ajouter une tâche"
          >
            <i className="bi bi-check2-square text-[#007BFF]" />
            <span className="hidden md:inline">Tâche</span>
          </button>
          <button
            onClick={onCalendar}
            data-testid="property-view-calendar-btn"
            className={CRM_BTN}
            title="Ajouter un événement"
          >
            <i className="bi bi-calendar3 text-[#007BFF]" />
            <span className="hidden md:inline">Agenda</span>
          </button>
          <button
            onClick={onAssign}
            data-testid="property-view-assign-btn"
            className={CRM_BTN}
            title="Affecter à un acquéreur"
          >
            <i className="bi bi-person-plus text-[#007BFF]" />
            <span className="hidden md:inline">Affecter</span>
          </button>
          <button
            onClick={() => (window.location.href = `/visites/nouvelle?bien=${property.id}&retour=/properties/${property.id}/view`)}
            data-testid="property-view-visite-btn"
            className={CRM_BTN}
            title="Enregistrer une visite"
          >
            <i className="bi bi-door-open text-[#007BFF]" />
            <span className="hidden md:inline">Visite</span>
          </button>
          <span className="hidden md:inline-block w-px h-6 bg-slate-200 mx-2" />
          <button
            onClick={() => setShowDiffusionModal(true)}
            data-testid="property-view-broadcast-btn"
            className={CRM_BTN_DIFFUSER}
            title="Gérer la diffusion sur les supports externes"
          >
            <i className="bi bi-broadcast text-[#28A745]" />
            <span className="hidden md:inline">Diffuser</span>
          </button>
          {property.created_at && negoFullName && (
            <span className="ml-auto text-xs text-slate-400 hidden lg:inline" data-testid="property-view-created-info">
              <i className="bi bi-info-circle" /> Saisi par <strong className="text-slate-700">{negoFullName}</strong> · le {new Date(property.created_at).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
      </section>

      {/* ═════════ KPIs DYNAMIQUES ═════════
          La rangée s'adapte au nombre réel de KPI (2 à 4 selon l'opération) :
          aucune case vide, aucun emplacement fantôme. Classes littérales
          (Tailwind ne détecte pas les noms de classes construits dynamiquement). */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-6 md:mt-8" data-testid="view-kpis">
        <div className={`grid gap-3 md:gap-4 ${KPI_GRID_CLASS[kpis.length] || KPI_GRID_CLASS[4]}`}>
          {kpis.map((k, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 md:p-5 border border-slate-200 min-w-0" data-testid={`view-kpi-${i}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <i className={`bi ${k.icon} ${k.iconClass}`} />
                <span className="truncate">{k.label}</span>
              </div>
              <div className="text-lg sm:text-xl md:text-3xl font-extrabold mt-1.5 text-slate-900 break-words leading-tight tabular-nums" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}>
                {k.value || '—'}
              </div>
              {k.sub && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{k.sub}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ═════════ TOC PILLS STICKY ═════════
          MOBILE : masqué (`hidden md:block`) — trop peu utile en mobile où
          l'utilisateur scrolle naturellement, et le strip mangeait de la
          hauteur d'écran pour peu de valeur ajoutée. Desktop : inchangé. */}
      <nav className="sticky top-0 z-20 bg-white/80 backdrop-blur border-y border-slate-100 mt-8 hidden md:block" data-testid="view-toc">
        <div ref={tocScrollRef} className="max-w-6xl mx-auto px-4 md:px-8 py-2 flex items-center gap-1 tabs-scroll-x">
          <TocPill href="#sec-apercu" icon="bi-eye" label="Aperçu" active={activeToc === 'apercu'} />
          <TocPill href="#sec-carac" icon="bi-list-check" label="Caractéristiques" active={activeToc === 'carac'} />
          {showFonds && <TocPill href="#sec-fonds" icon="bi-briefcase" label="Le fonds" active={activeToc === 'fonds'} />}
          {showBail && <TocPill href="#sec-bail" icon="bi-file-earmark-text" label="Bail" active={activeToc === 'bail'} />}
          {showMandat && <TocPill href="#sec-mandat" icon="bi-bookmark-check" label="Mandat" active={activeToc === 'mandat'} />}
          {showPerf && <TocPill href="#sec-perf" icon="bi-bar-chart" label="Performance" active={activeToc === 'perf'} />}
          <TocPill href="#sec-confid" icon="bi-shield-lock" label="Confidentiel" active={activeToc === 'confid'} />
          <TocPill href="#sec-historique" icon="bi-clock-history" label="Historique" active={activeToc === 'historique'} />
          {photos.length > 0 && <TocPill href="#sec-photos" icon="bi-images" label="Photos" active={activeToc === 'photos'} />}
        </div>
      </nav>

      {/* ═════════ CONTENT GRID 2/3 + 1/3 ═════════ */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ─────── COL PRINCIPALE ─────── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Le bien en un coup d'œil */}
          {bg.description_courte && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="view-section-apercu">
              <SectionTitle>Le bien en un coup d&apos;œil</SectionTitle>
              <p className="text-slate-700 leading-relaxed text-[15px] mt-4">{bg.description_courte}</p>
            </article>
          )}

          {/* Description détaillée */}
          {description_detaillee && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="view-section-description">
              <SectionTitle>Description détaillée</SectionTitle>
              <div
                className="text-slate-700 leading-relaxed text-[14.5px] mt-4 text-left [&>p]:mb-2 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>h3]:font-bold [&>h3]:text-base"
                dangerouslySetInnerHTML={{ __html: renderDescriptionHtml(description_detaillee) }}
              />
            </article>
          )}

          {/* Composition COMPLÈTE — Surfaces, structurelles, capacités, équipements */}
          <article id="sec-carac" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-carac">
            <SectionTitle>Composition du bien</SectionTitle>

            {surfaces.length > 0 && (
              <div className="mt-5">
                <h4 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Surfaces</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {surfaces.map((s, i) => (
                    <div key={i} className={`rounded-lg p-3 ${s.highlight ? 'bg-blue-50 ring-1 ring-blue-200' : s.accent === 'emerald' ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-slate-50'}`}>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{s.label}</div>
                      <div className={`text-lg font-bold mt-0.5 ${s.highlight ? 'text-blue-700' : s.accent === 'emerald' ? 'text-emerald-700' : 'text-slate-900'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {fmtNumber(s.value)} {s.unit}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {structurelles.length > 0 && (
              <div className="mt-6">
                <h4 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Caractéristiques structurelles</h4>
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {structurelles.map((c, i) => (
                    <div key={i}>
                      <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</dt>
                      <dd className="font-semibold text-slate-900 mt-0.5">{c.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {capacitesNumber.length > 0 && (
              <div className="mt-6">
                <h4 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Capacités</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {capacitesNumber.map((c, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{c.label}</div>
                      <div className="text-2xl font-bold mt-0.5 text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {fmtNumber(c.value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {equipementsRendered.length > 0 && (
              <div className="mt-6" data-testid="view-section-equipements">
                <h4 className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Équipements &amp; options</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 grid-flow-row-dense gap-x-4 gap-y-2 text-sm">
                  {[...equipementsRendered.filter((e) => !e.quals), ...equipementsRendered.filter((e) => e.quals)].map((e, i) => (
                    <div key={i} className={`flex items-center gap-2 ${e.quals ? 'col-span-2 md:col-span-3 flex-wrap gap-y-1.5' : (e.text && e.label.length + e.text.length > 24 ? 'col-span-2' : '')}`}>
                      {e.quals ? (
                        <>
                          <i className="bi bi-dot text-slate-400" />
                          <span className="text-slate-500 shrink-0 whitespace-nowrap">{e.label} :</span>
                          {e.quals.map((q, j) => (
                            <Chip key={j} className="bg-slate-100 text-slate-600">{q}</Chip>
                          ))}
                        </>
                      ) : e.text ? (
                        <>
                          <i className="bi bi-dot text-slate-400" />
                          <span className="text-slate-500 min-w-0 break-words">{e.label} :</span>
                          <span className="font-semibold text-slate-900 min-w-0 break-words">{e.text}</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle-fill text-slate-500" />
                          <span className="font-medium text-slate-700">{e.label}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Diagnostics (DPE/GES) — exclus pour licence et parts sociales (actifs incorporels) */}
            {!looksLikeLicence && !looksLikePartsSociales && (bg.dpe || bg.ges || bg.dpe_date || bg.ges_date) && (
              <div className="mt-6 pt-5 border-t border-slate-100">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Diagnostics</span>
                <div className="flex items-center gap-4 flex-wrap mt-2">
                  {bg.dpe && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                      <span className="text-slate-600">DPE</span>
                      <span className="px-2 py-1 rounded-md text-white font-bold text-xs" style={{ background: { A: '#16A34A', B: '#84CC16', C: '#FACC15', D: '#F59E0B', E: '#F97316', F: '#EF4444', G: '#B91C1C' }[bg.dpe] || '#94A3B8' }}>
                        {bg.dpe}
                      </span>
                      {bg.dpe_date && <span className="text-slate-500 font-normal">({new Date(bg.dpe_date).toLocaleDateString('fr-FR')})</span>}
                    </span>
                  )}
                  {bg.ges && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                      <span className="text-slate-600">GES</span>
                      <span className="px-2 py-1 rounded-md text-white font-bold text-xs" style={{ background: { A: '#16A34A', B: '#84CC16', C: '#FACC15', D: '#F59E0B', E: '#F97316', F: '#EF4444', G: '#B91C1C' }[bg.ges] || '#94A3B8' }}>
                        {bg.ges}
                      </span>
                      {bg.ges_date && <span className="text-slate-500 font-normal">({new Date(bg.ges_date).toLocaleDateString('fr-FR')})</span>}
                    </span>
                  )}
                </div>
              </div>
            )}

            {surfaces.length === 0 && structurelles.length === 0 && capacitesNumber.length === 0 && equipementsRendered.length === 0 && (
              <p className="text-sm text-slate-400 italic mt-4">Aucune information de composition renseignée pour le moment.</p>
            )}
          </article>

          {/* lot1 — Ordre imposé : 1. Caractéristiques du bien · 2. Caractéristiques
              d'exploitation · 3. Le fonds · 4. Commissions · 5. Bail · 6. Chiffres. */}
          {capaciteCHR.length > 0 && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="view-section-capacite">
              <SectionTitle>Caractéristiques d&apos;exploitation</SectionTitle>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {capaciteCHR.map((c, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-500">{c.label}</div>
                    <div className="text-2xl font-bold mt-0.5 text-slate-900 tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {c.value}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          )}

          {/* Exploitation — Le fonds (interne) : même rangement que la saisie (iter132) */}
          {showFonds && (
            <article id="sec-fonds" className="bg-white rounded-2xl border border-amber-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-fonds">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}>Exploitation — Le fonds</h2>
                {(confid.effectif_salaries != null || confid.detail_salaries) && (
                  <PublicationState published={effectifPublie} label="Effectif" data-testid="effectif-publication-state" />
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {confid.effectif_salaries != null && (
                  <div>
                    <div className="text-xs text-slate-500">Effectif salariés</div>
                    <div className="font-bold mt-1 tabular-nums" data-testid="confid-effectif-salaries">{confid.effectif_salaries}</div>
                  </div>
                )}
                {confid.detail_salaries && (
                  <div data-testid="confid-detail-salaries">
                    <div className="text-xs text-slate-500">Détail de l&apos;effectif</div>
                    <div className="font-bold mt-1 whitespace-pre-line">{confid.detail_salaries}</div>
                  </div>
                )}
                {confid.date_creation_etablissement && (
                  <div>
                    <div className="text-xs text-slate-500">Date de création</div>
                    <div className="font-bold mt-1 tabular-nums">{new Date(confid.date_creation_etablissement).toLocaleDateString('fr-FR')}</div>
                  </div>
                )}
                {confid.valeur_stock != null && (
                  <div>
                    <div className="text-xs text-slate-500">Valeur du stock</div>
                    <div className="font-bold mt-1 tabular-nums">{fmtEuro(confid.valeur_stock)}</div>
                  </div>
                )}
              </div>
            </article>
          )}

          {/* Commissions (tabac / presse / Loto-FDJ / PMU) — bloc dédié : un montant en
              euros n'est pas une capacité d'accueil. */}
          {commissions.length > 0 && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="view-section-commissions">
              <SectionTitle>Commissions</SectionTitle>
              <dl className="divide-y divide-slate-100 text-sm mt-4">
                {commissions.map((c) => (
                  <div key={c.label} className="py-3 flex justify-between gap-4">
                    <dt className="text-slate-500">{c.label}</dt>
                    <dd className="font-semibold text-slate-900 tabular-nums">{c.value}</dd>
                  </div>
                ))}
              </dl>
            </article>
          )}

          {/* Bail */}
          {showBail && (
            <article id="sec-bail" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-bail">
              <SectionTitle>Bail &amp; éléments juridiques</SectionTitle>
              <dl className="divide-y divide-slate-100 text-sm mt-4">
                {titreLocatifType && <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Type de bail</dt><dd className="font-semibold text-slate-900 text-right">{titreLocatifType}</dd></div>}
                {titreLocatifDuree && <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Durée du bail</dt><dd className="font-semibold text-slate-900 text-right">{titreLocatifDuree}</dd></div>}
                {bail.date_debut_bail && <div className="py-3 flex justify-between"><dt className="text-slate-500">Date de début</dt><dd className="font-semibold text-slate-900">{new Date(bail.date_debut_bail).toLocaleDateString('fr-FR')}</dd></div>}
                {bail.date_echeance_bail && <div className="py-3 flex justify-between"><dt className="text-slate-500">Date d&apos;échéance</dt><dd className="font-semibold text-slate-900 tabular-nums" data-testid="view-bail-echeance">{new Date(bail.date_echeance_bail).toLocaleDateString('fr-FR')}</dd></div>}
                {titreLocatifDestination && <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Destination</dt><dd className="font-semibold text-slate-900 text-right">{titreLocatifDestination}</dd></div>}
                {titreLocatifIndexation && <div className="py-3 flex justify-between"><dt className="text-slate-500">Indexation</dt><dd className="font-semibold text-slate-900">{titreLocatifIndexation}</dd></div>}
                {bail.loyer_mensuel != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Loyer mensuel HT</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.loyer_mensuel)}</dd></div>}
                {bail.loyer_annuel != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Loyer annuel HT</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.loyer_annuel)}</dd></div>}
                {(bail.charges != null || bail.charges_mensuelles != null) && <div className="py-3 flex justify-between"><dt className="text-slate-500">Charges /mois</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.charges_mensuelles != null ? bail.charges_mensuelles : bail.charges)}</dd></div>}
                {bail.loyer_soumis_tva && <div className="py-3 flex justify-between"><dt className="text-slate-500">Loyer soumis à TVA</dt><dd className="font-semibold text-slate-900">{bail.loyer_soumis_tva}</dd></div>}
                {bail.depot_garantie != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Dépôt de garantie</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.depot_garantie)}</dd></div>}
                {bail.droit_entree != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Pas-de-porte / Droit d&apos;entrée</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.droit_entree)}</dd></div>}
                {bail.redevance_mensuelle != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Redevance mensuelle (gérance)</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.redevance_mensuelle)}</dd></div>}
                {bail.redevance_annuelle != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Redevance annuelle</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.redevance_annuelle)}</dd></div>}
                {bail.duree_gerance && <div className="py-3 flex justify-between"><dt className="text-slate-500">Durée de la gérance</dt><dd className="font-semibold text-slate-900">{bail.duree_gerance}</dd></div>}
                {bail.caution_gerance != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Caution gérance</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.caution_gerance)}</dd></div>}
                {bail.impot_foncier != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Impôt foncier</dt><dd className="font-semibold text-slate-900">{fmtEuro(bail.impot_foncier)}</dd></div>}
                {bail.conditions_particulieres_gerance && (
                  <div className="py-3"><dt className="text-slate-500 mb-1">Conditions particulières (gérance)</dt><dd className="font-medium text-slate-700 text-sm">{bail.conditions_particulieres_gerance}</dd></div>
                )}
              </dl>
            </article>
          )}

          {/* Conditions de vente (cession & vente) — incluant les variantes legacy. Honoraires déplacés dans section Confidentiel. */}
          {(isCessionFamily(operation) || isVenteImmoFamily(operation) || operation === 'parts_sociales' || operation === 'licence') && (vente.tva_applicable != null || vente.conditions_particulieres || vente.delai_liberation || vente.date_disponibilite || vente.taux_rendement_net != null || vente.nombre_parts_sociales != null || vente.fonds_detenu_par_societe != null || vente.murs_detenus_par_societe != null || vente.net_vendeur != null || vente.prix_minimum_accepte != null || vente.charges_annuelles != null || vente.taxe_fonciere != null || vente.loyer_annuel_actuel != null || vente.taux_rendement_brut != null || vente.negociable != null) && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="view-section-conditions-vente">
              <SectionTitle>Conditions de vente</SectionTitle>
              <dl className="divide-y divide-slate-100 text-sm mt-4">
                {/* iter130 — « Type de prix » retiré : l'information découle de la charge des honoraires (bloc Honoraires d'agence) */}
                {vente.net_vendeur != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Prix net vendeur</dt><dd className="font-semibold text-slate-900">{fmtEuro(vente.net_vendeur)}</dd></div>}
                {vente.prix_minimum_accepte != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Prix minimum accepté</dt><dd className="font-semibold text-slate-900">{fmtEuro(vente.prix_minimum_accepte)}</dd></div>}
                {vente.tva_applicable != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">TVA applicable</dt><dd className="font-semibold text-slate-900">{vente.tva_applicable ? `Oui${vente.taux_tva ? ` (${vente.taux_tva}%)` : ''}` : 'Non'}</dd></div>}
                {vente.charges_annuelles != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Charges annuelles</dt><dd className="font-semibold text-slate-900">{fmtEuro(vente.charges_annuelles)}</dd></div>}
                {vente.taxe_fonciere != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Taxe foncière</dt><dd className="font-semibold text-slate-900">{fmtEuro(vente.taxe_fonciere)}</dd></div>}
                {vente.loyer_annuel_actuel != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Loyer annuel actuel</dt><dd className="font-semibold text-slate-900">{fmtEuro(vente.loyer_annuel_actuel)}</dd></div>}
                {vente.taux_rendement_brut != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Rendement brut</dt><dd className="font-semibold text-slate-900">{vente.taux_rendement_brut}%</dd></div>}
                {vente.taux_rendement_net != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Rendement net</dt><dd className="font-semibold text-slate-900">{vente.taux_rendement_net}%</dd></div>}
                {vente.date_disponibilite && <div className="py-3 flex justify-between"><dt className="text-slate-500">Date de disponibilité</dt><dd className="font-semibold text-slate-900">{new Date(vente.date_disponibilite).toLocaleDateString('fr-FR')}</dd></div>}
                {vente.delai_liberation && <div className="py-3 flex justify-between"><dt className="text-slate-500">Délai de libération</dt><dd className="font-semibold text-slate-900">{vente.delai_liberation}</dd></div>}
                {vente.negociable != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Prix négociable</dt><dd className="font-semibold text-slate-900">{vente.negociable ? 'Oui' : 'Non'}</dd></div>}
                {/* Parts sociales : détails de structure de société */}
                {vente.nombre_parts_sociales != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Nombre de parts sociales</dt><dd className="font-semibold text-slate-900">{vente.nombre_parts_sociales}</dd></div>}
                {vente.fonds_detenu_par_societe != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Fonds détenu par société</dt><dd className="font-semibold text-slate-900">{vente.fonds_detenu_par_societe ? 'Oui' : 'Non'}</dd></div>}
                {vente.murs_detenus_par_societe != null && <div className="py-3 flex justify-between"><dt className="text-slate-500">Murs détenus par société</dt><dd className="font-semibold text-slate-900">{vente.murs_detenus_par_societe ? 'Oui' : 'Non'}</dd></div>}
                {vente.conditions_particulieres && (
                  <div className="py-3"><dt className="text-slate-500 mb-1">Conditions particulières</dt><dd className="font-medium text-slate-700 text-sm">{vente.conditions_particulieres}</dd></div>
                )}
              </dl>
            </article>
          )}

          {/* Section LICENCE — uniquement pour les actifs incorporels (licence d'exploitation) */}
          {(operation === 'licence' || isLicenceAsset(taxonomy)) && (bg.transferable || bg.commune_rattachement || bg.conditions_transfert || bg.type_licence || bg.licence_restaurant || bg.date_derniere_exploitation) && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="view-section-licence">
              <SectionTitle>Détails de la licence</SectionTitle>
              <dl className="divide-y divide-slate-100 text-sm mt-4">
                {(bg.licence_restaurant || bg.type_licence) && <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Type de licence</dt><dd className="font-semibold text-slate-900 text-right">{bg.licence_restaurant || bg.type_licence}</dd></div>}
                {bg.commune_rattachement && <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Commune de rattachement</dt><dd className="font-semibold text-slate-900 text-right">{bg.commune_rattachement}</dd></div>}
                {bg.transferable && <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Transférable</dt><dd className="font-semibold text-slate-900 text-right">{bg.transferable}</dd></div>}
                {bg.date_derniere_exploitation && <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Dernière exploitation</dt><dd className="font-semibold text-slate-900 text-right">{new Date(bg.date_derniere_exploitation).toLocaleDateString('fr-FR')}</dd></div>}
                {bg.conditions_transfert && (
                  <div className="py-3"><dt className="text-slate-500 mb-1">Conditions de transfert</dt><dd className="font-medium text-slate-700 text-sm whitespace-pre-line">{bg.conditions_transfert}</dd></div>
                )}
              </dl>
            </article>
          )}

          {/* Honoraires d'agence (section dédiée, visible en interne — auparavant dans Confidentiel) */}
          {(() => {
            const isLocOp = ['location', 'location_droit_entree'].includes(operation);
            const isVenteOp = ['cession', 'vente_immo', 'vente_murs', 'parts_sociales', 'licence', 'location_gerance'].includes(operation);
            if (!isLocOp && !isVenteOp) return null;

            // Modèle d'honoraires : SOURCE UNIQUE `utils/honorairesModel.js`.
            // ⚠️ En location, la commission est due UNE SEULE FOIS à la signature du bail :
            // elle n'est PAS mensuelle. Base du pourcentage = loyer ANNUEL HT.
            const hv = deriveHonorairesView(property);
            if (!hv.hasAny) return null;

            return (
              <article id="sec-honoraires" className="bg-white rounded-2xl border border-amber-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-honoraires">
                <div className="flex items-center justify-between mb-3">
                  <SectionTitle>Honoraires d&apos;agence</SectionTitle>
                  <i className="bi bi-receipt text-amber-600 text-xl"></i>
                </div>

                {/* Montant TTC + taux (le taux porte sur le PRIX NET VENDEUR — iter123) */}
                {(hv.ttc != null || hv.pct != null) && (
                  <div className="mt-2 mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                      {hv.ttc != null && (
                        <div>
                          <div className="text-xs text-amber-800/80 uppercase tracking-wide font-semibold">Honoraires TTC</div>
                          <div className="text-2xl sm:text-3xl font-bold text-slate-900 mt-0.5 tabular-nums" data-testid="view-honoraires-ttc">
                            {formatEuroFR(hv.ttc)}
                          </div>
                          {hv.ht != null && (
                            <div className="text-xs text-slate-600 mt-1" data-testid="view-honoraires-ht">
                              {formatEuroFR(hv.ht)} HT + {formatEuroFR(hv.tva_montant)} de TVA ({hv.tva_taux}&nbsp;%)
                            </div>
                          )}
                        </div>
                      )}
                      {hv.pct != null && (
                        <div className={hv.ttc != null ? 'text-right' : ''}>
                          <div className="text-xs text-amber-800/80 uppercase tracking-wide font-semibold">Taux</div>
                          <div className="text-2xl sm:text-3xl font-bold text-slate-900 mt-0.5 tabular-nums" data-testid="view-honoraires-taux">
                            {formatPercentFR(hv.pct)}
                          </div>
                          <div className="text-xs text-slate-600 mt-1">
                            {hv.pctBaseLabel}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {hv.base != null && (
                    <div className="py-2 px-3 rounded-lg bg-slate-50 border border-slate-200 flex justify-between gap-3" data-testid="view-honoraires-base">
                      <dt className="text-slate-500 text-xs sm:text-sm">Base de calcul ({hv.baseLabel})</dt>
                      <dd className="font-semibold text-slate-900 text-sm text-right tabular-nums">{formatEuroFR(hv.base)}</dd>
                    </div>
                  )}
                  {hv.type && (
                    <div className="py-2 px-3 rounded-lg bg-slate-50 border border-slate-200 flex justify-between gap-3">
                      <dt className="text-slate-500 text-xs sm:text-sm">Type d&apos;honoraires</dt>
                      <dd className="font-semibold text-slate-900 text-sm text-right capitalize">{hv.type}</dd>
                    </div>
                  )}
                  {hv.charge && (
                    <div className="py-2 px-3 rounded-lg bg-slate-50 border border-slate-200 flex justify-between gap-3" data-testid="view-honoraires-charge">
                      <dt className="text-slate-500 text-xs sm:text-sm">À la charge de</dt>
                      <dd className="font-semibold text-slate-900 text-sm text-right">{hv.chargeLabel}</dd>
                    </div>
                  )}
                  {!hv.isLoc && hv.pnv != null && (
                    <div className="py-2 px-3 rounded-lg bg-slate-50 border border-slate-200 flex justify-between gap-3" data-testid="view-honoraires-net-vendeur">
                      <dt className="text-slate-500 text-xs sm:text-sm">Prix net vendeur</dt>
                      <dd className="font-semibold text-slate-900 text-sm text-right tabular-nums">{formatEuroFR(hv.pnv)}</dd>
                    </div>
                  )}
                  {!hv.isLoc && hv.fai != null && (
                    <div className="py-2 px-3 rounded-lg bg-slate-50 border border-slate-200 flex justify-between gap-3" data-testid="view-honoraires-fai">
                      <dt className="text-slate-500 text-xs sm:text-sm">{libellePrixTotal(hv.charge)} (calculé : net vendeur + honoraires)</dt>
                      <dd className="font-semibold text-slate-900 text-sm text-right tabular-nums">{formatEuroFR(hv.fai)}</dd>
                    </div>
                  )}
                </dl>
                {/* lot1 — note de développeur retirée de l'interface (elle n'a rien à dire à
                    un négociateur). Source unique : `informations_bail.honoraires_*` en location,
                    `informations_vente.honoraires_*` sinon — cf. HONORAIRES-source-de-verite.md */}
              </article>
            );
          })()}

          {/* Mandat & Négociateur */}
          {showMandat && (
            <article id="sec-mandat" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-mandat">
              <SectionTitle>Informations du mandat</SectionTitle>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4">
                {mandatNumero && (
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Numéro</dt>
                    <dd className="font-semibold mt-0.5">{mandatNumero}</dd>
                  </div>
                )}
                {mandatType && (
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type</dt>
                    <dd className="font-semibold mt-0.5 inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      {mandatType}
                    </dd>
                  </div>
                )}
                {mandatDateSig && (
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Date de signature</dt>
                    <dd className="font-semibold mt-0.5">{new Date(mandatDateSig).toLocaleDateString('fr-FR')}</dd>
                  </div>
                )}
                {mandatDateFin && (
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Échéance</dt>
                    <dd className="font-semibold mt-0.5">{new Date(mandatDateFin).toLocaleDateString('fr-FR')}</dd>
                  </div>
                )}
              </dl>
              <LinkedMandatDocs propertyId={property.id} />
            </article>
          )}

          {/* Performance financière (interne) — iter133 : un seul tableau, années en colonnes */}
          {showPerf && (
            <article id="sec-perf" className="bg-white rounded-2xl border border-amber-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-perf">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}>Performance financière</h2>
                <PublicationState published={caPublie} label="Chiffres" data-testid="perf-publication-state" />
              </div>
              <ChiffresTableView rows={perfRows} />
              {confid.banque && (
                <div className="mt-4 pt-3 border-t border-slate-200 text-xs">
                  <span className="text-slate-500">Banque : </span><span className="font-semibold">{confid.banque}</span>
                </div>
              )}
            </article>
          )}

          {/* Conditions de visite (interne) */}
          {canViewConfidential && (confid.conditions_visites || confid.horaires_visites || confid.contact_visites || confid.remarques_visites || vente.conditions_visite || vente.contact_visite) && (
            <article className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8" data-testid="view-section-visites">
              <SectionTitle>Conditions de visite</SectionTitle>
              <dl className="divide-y divide-slate-100 text-sm mt-4">
                {(confid.conditions_visites || vente.conditions_visite) && (
                  <div className="py-3"><dt className="text-slate-500 mb-1">Conditions</dt><dd className="font-medium text-slate-700">{confid.conditions_visites || vente.conditions_visite}</dd></div>
                )}
                {confid.horaires_visites && (
                  <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Horaires</dt><dd className="font-semibold text-slate-900 text-right">{confid.horaires_visites}</dd></div>
                )}
                {(confid.contact_visites || vente.contact_visite) && (
                  <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Contact</dt><dd className="font-semibold text-slate-900 text-right">{confid.contact_visites || vente.contact_visite}</dd></div>
                )}
                {confid.remarques_visites && (
                  <div className="py-3"><dt className="text-slate-500 mb-1">Remarques</dt><dd className="font-medium text-slate-700">{confid.remarques_visites}</dd></div>
                )}
              </dl>
            </article>
          )}

          {/* Confidentiel — verrouillé ou ouvert */}
          {!canViewConfidential ? (
            <article id="sec-confid" className="rounded-2xl border-2 border-dashed bg-amber-50 border-amber-300 scroll-mt-32 p-6 text-center" data-testid="view-section-confid-locked">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-200 flex items-center justify-center">
                <i className="bi bi-shield-lock-fill text-amber-700 text-xl" />
              </div>
              <h3 className="font-bold text-amber-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Informations confidentielles verrouillées</h3>
              <p className="text-sm text-amber-800 mt-1">Adresse exacte, identité du cédant, conditions de visite.</p>
              {onShowConfidential && (
                <button
                  onClick={onShowConfidential}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold transition"
                  data-testid="view-confid-unlock-btn"
                >
                  <i className="bi bi-eye-fill" />Afficher le confidentiel
                </button>
              )}
            </article>
          ) : (
            (confidentialLoc.street || confid.adresse || confid.adresse_bien_confidentielle || confid.coordonnees_gps || confid.proprietaire?.nom || confid.proprietaire?.societe || confid.conditions_visites || confid.raison_vente || confid.procedure || confid.syndic_nom || confid.informations_diverses || confid.franchise || confid.compte_bancaire || confid.caution_fournisseurs != null || confid.valeur_estimee != null || confid.dernier_prix_transaction != null || confid.frais_annuels != null || confid.date_visite || confid.notes_visite || confid.notes_negociateur) && (
              <article id="sec-confid" className="bg-white rounded-2xl border-2 border-amber-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-confid">
                <div className="flex items-center justify-between mb-4">
                  <SectionTitle>Confidentiel</SectionTitle>
                  <Chip className="bg-amber-100 text-amber-700"><i className="bi bi-shield-lock-fill" />Privé</Chip>
                </div>
                <dl className="divide-y divide-slate-100 text-sm mt-2">
                  {(confidentialLoc.street || confid.adresse) && (
                    <div className="py-3 flex justify-between gap-4">
                      <dt className="text-slate-500">Adresse exacte</dt>
                      <dd className="font-semibold text-slate-900 text-right">
                        {/* ⚠️ `confid.adresse` est parfois un OBJET (biens legacy) : rendu tel
                            quel, il fait planter toute la fiche. */}
                        {formatAdresse(confidentialLoc.street || confid.adresse)}
                        {(confidentialLoc.zip || confid.code_postal) ? `, ${confidentialLoc.zip || confid.code_postal}` : ''}
                        {(confidentialLoc.city || confid.ville) ? ` ${confidentialLoc.city || confid.ville}` : ''}
                      </dd>
                    </div>
                  )}
                  {(confid.proprietaire?.nom || confid.proprietaire?.societe) && (
                    <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Propriétaire</dt><dd className="font-semibold text-slate-900 text-right" data-testid="view-proprietaire-label">{getContactFullLabel(confid.proprietaire)}</dd></div>
                  )}
                  {confid.proprietaire?.telephone && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Téléphone propriétaire</dt><dd className="font-semibold text-slate-900 num">{formatPhoneFR(confid.proprietaire.telephone)}</dd></div>
                  )}
                  {confid.proprietaire?.email && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Email propriétaire</dt><dd className="font-semibold text-slate-900">{confid.proprietaire.email}</dd></div>
                  )}
                  {/* Honoraires d'agence : déplacés dans une section dédiée visible en finance (cf. sec-honoraires) */}
                  {/* iter122 — Vente des murs (ex-bloc « Divers » de l'onglet Détails) : levier de négociation, jamais public */}
                  {(property.exploitation || {}).vente_murs_potentielle && (
                    <div className="py-3 flex justify-between gap-4" data-testid="view-vente-murs">
                      <dt className="text-slate-500">Vente potentielle des murs</dt>
                      <dd className="font-semibold text-slate-900">{property.exploitation.vente_murs_potentielle}</dd>
                    </div>
                  )}
                  {(property.exploitation || {}).prix_murs != null && (
                    <div className="py-3 flex justify-between gap-4" data-testid="view-prix-murs">
                      <dt className="text-slate-500">Prix des murs</dt>
                      <dd className="font-semibold text-slate-900 tabular-nums">{fmtEuro(property.exploitation.prix_murs)}</dd>
                    </div>
                  )}
                  {confid.raison_vente && (
                    <div className="py-3"><dt className="text-slate-500 mb-1">Raison de la vente</dt><dd className="font-medium text-slate-700">{confid.raison_vente}</dd></div>
                  )}
                  {confid.procedure && (
                    <div className="py-3 flex justify-between gap-4 items-center">
                      <dt className="text-slate-500">Procédure (bail / fonds)</dt>
                      <dd>
                        {(() => {
                          const alerte = ['Dépôt de bilan', 'Liquidation judiciaire'].includes(confid.procedure);
                          const cls = alerte ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700';
                          return (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}>
                              <i className={`bi ${alerte ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill'}`} />
                              {confid.procedure}
                            </span>
                          );
                        })()}
                      </dd>
                    </div>
                  )}
                  {confid.syndic_nom && (
                    <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Syndic</dt><dd className="font-semibold text-slate-900 text-right">{confid.syndic_nom}{confid.syndic_adresse ? ` — ${confid.syndic_adresse}` : ''}</dd></div>
                  )}
                  {(confid.syndic_telephone || confid.syndic_email) && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Contact syndic</dt><dd className="font-semibold text-slate-900 text-right">{[confid.syndic_telephone, confid.syndic_email].filter(Boolean).join(' · ')}</dd></div>
                  )}
                  {confid.banque && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Banque</dt><dd className="font-semibold text-slate-900">{confid.banque}</dd></div>
                  )}
                  {confid.commentaires_confidentiels && (
                    <div className="py-3"><dt className="text-slate-500 mb-1">Commentaires confidentiels</dt><dd className="font-medium text-slate-700">{confid.commentaires_confidentiels}</dd></div>
                  )}
                  {confid.notes_particulieres && (
                    <div className="py-3"><dt className="text-slate-500 mb-1">Notes particulières</dt><dd className="font-medium text-slate-700">{confid.notes_particulieres}</dd></div>
                  )}
                  {/* iter129 — L'enseigne (nom en devanture) identifie le commerce :
                      elle n'est affichée QUE dans les informations confidentielles. */}
                  {bg.enseigne && (
                    <div className="py-3 flex justify-between gap-4" data-testid="confid-enseigne">
                      <dt className="text-slate-500">Enseigne</dt>
                      <dd className="font-semibold text-slate-900 text-right">{bg.enseigne}</dd>
                    </div>
                  )}
                  {confid.adresse_bien_confidentielle && confid.adresse_bien_confidentielle !== (confidentialLoc.street || confid.adresse) && (
                    <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Adresse du bien (confid.)</dt><dd className="font-semibold text-slate-900 text-right">{confid.adresse_bien_confidentielle}</dd></div>
                  )}
                  {confid.coordonnees_gps && (
                    <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Coordonnées GPS</dt><dd className="font-semibold text-slate-900 text-right num">{confid.coordonnees_gps}</dd></div>
                  )}
                  {(confid.syndic_contacts?.telephone || confid.syndic_contacts?.email || confid.syndic_contacts?.contact) && (
                    <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Contacts syndic</dt><dd className="font-semibold text-slate-900 text-right">{[confid.syndic_contacts.contact, confid.syndic_contacts.telephone, confid.syndic_contacts.email].filter(Boolean).join(' · ')}</dd></div>
                  )}
                  {confid.compte_bancaire && (
                    <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Compte bancaire</dt><dd className="font-semibold text-slate-900 text-right num">{confid.compte_bancaire}</dd></div>
                  )}
                  {confid.franchise && (
                    <div className="py-3 flex justify-between gap-4"><dt className="text-slate-500">Franchise / réseau</dt><dd className="font-semibold text-slate-900 text-right">{confid.franchise}</dd></div>
                  )}
                  {confid.caution_fournisseurs != null && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Caution fournisseurs</dt><dd className="font-semibold text-slate-900">{fmtEuro(confid.caution_fournisseurs)}</dd></div>
                  )}
                  {confid.valeur_estimee != null && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Valeur estimée (licence)</dt><dd className="font-semibold text-slate-900">{fmtEuro(confid.valeur_estimee)}</dd></div>
                  )}
                  {confid.dernier_prix_transaction != null && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Dernier prix de transaction</dt><dd className="font-semibold text-slate-900">{fmtEuro(confid.dernier_prix_transaction)}</dd></div>
                  )}
                  {confid.frais_annuels != null && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Frais annuels (licence)</dt><dd className="font-semibold text-slate-900">{fmtEuro(confid.frais_annuels)}</dd></div>
                  )}
                  {confid.date_visite && (
                    <div className="py-3 flex justify-between"><dt className="text-slate-500">Date de visite</dt><dd className="font-semibold text-slate-900">{new Date(confid.date_visite).toLocaleDateString('fr-FR')}</dd></div>
                  )}
                  {confid.notes_visite && (
                    <div className="py-3"><dt className="text-slate-500 mb-1">Notes de visite</dt><dd className="font-medium text-slate-700">{confid.notes_visite}</dd></div>
                  )}
                  {confid.informations_diverses && (
                    <div className="py-3"><dt className="text-slate-500 mb-1">Informations diverses</dt><dd className="font-medium text-slate-700 whitespace-pre-line">{confid.informations_diverses}</dd></div>
                  )}
                  {(confid.notes_negociateur || property.notes_negociateur) && (
                    <div className="py-3"><dt className="text-slate-500 mb-1 inline-flex items-center gap-1.5"><i className="bi bi-person-badge text-amber-600" />Notes internes négociateur</dt><dd className="font-medium text-slate-700 whitespace-pre-line">{confid.notes_negociateur || property.notes_negociateur}</dd></div>
                  )}
                </dl>
              </article>
            )
          )}

          {/* Historique simple */}
          <article id="sec-historique" className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 scroll-mt-32" data-testid="view-section-historique">
            <div className="flex items-center justify-between mb-5">
              <SectionTitle>Historique</SectionTitle>
            </div>
            <dl className="divide-y divide-slate-100 text-sm">
              {(() => {
                // lot DA2 — UNE seule liste, triée de la plus récente à la plus ancienne, tous
                // types confondus. « Création du bien » reste toujours la dernière ligne.
                const fmtD = (t) => (t ? new Date(t).toLocaleDateString('fr-FR') : '');
                const temps = (d) => (d ? new Date(d).getTime() : 0);
                const lignes = [];

                for (const e of (property.historique_activites || [])) {
                  if (!['document', 'visite', 'denonce'].includes(e.type)) continue;
                  lignes.push({
                    cle: `act-${lignes.length}`,
                    icone: e.type === 'visite' ? 'bi-door-open text-blue-500'
                      : e.type === 'denonce' ? 'bi-envelope-check text-[#0062CC]'
                        : e.action === 'deleted' ? 'bi-trash text-rose-500'
                          : e.action === 'renamed' || e.action === 'updated' ? 'bi-pencil text-blue-500'
                            : 'bi-paperclip text-slate-500',
                    libelle: e.label,
                    valeur: fmtD(e.date),
                    tri: temps(e.date),
                    visiteId: e.visite_id || null,
                    testid: 'view-history-doc-event',
                  });
                }

                // Envois de fiche regroupés par destinataire (évite le doublonnage).
                const envois = Array.isArray(property.historique_envois) ? property.historique_envois : [];
                const groupes = new Map();
                for (const e of envois) {
                  const dests = Array.isArray(e.destinataires) ? e.destinataires : [];
                  const cle = dests.join(', ') || '—';
                  const t = temps(e.date);
                  if (!groupes.has(cle)) groupes.set(cle, { cle, count: 0, min: t, max: t });
                  const g = groupes.get(cle);
                  g.count += 1;
                  if (t && (!g.min || t < g.min)) g.min = t;
                  if (t && (!g.max || t > g.max)) g.max = t;
                }
                for (const g of groupes.values()) {
                  lignes.push({
                    cle: `env-${g.cle}`,
                    icone: 'bi-envelope-check text-blue-500',
                    libelle: (
                      <>
                        Fiche envoyée{g.count > 1 ? <> <span className="font-medium text-slate-700">{g.count}×</span></> : null} à{' '}
                        <span className="font-medium text-slate-700">{g.cle}</span>
                      </>
                    ),
                    valeur: g.count > 1 && g.min !== g.max
                      ? <>du {fmtD(g.min)}<br />au {fmtD(g.max)}</>
                      : fmtD(g.max),
                    tri: g.max || 0,
                    testid: 'view-history-email-event',
                  });
                }

                if (property.updated_at) {
                  lignes.push({
                    cle: 'maj',
                    icone: 'bi-pencil text-blue-500',
                    libelle: 'Dernière modification',
                    valeur: fmtD(property.updated_at),
                    tri: temps(property.updated_at),
                    testid: 'view-history-updated-event',
                  });
                }

                lignes.sort((a, b) => b.tri - a.tri);

                if (property.created_at) {
                  lignes.push({
                    cle: 'creation',
                    icone: 'bi-plus-circle text-emerald-500',
                    libelle: 'Création du bien',
                    valeur: `${fmtD(property.created_at)}${negoFullName ? ` par ${negoFullName}` : ''}`,
                    tri: -1,
                    testid: 'view-history-created-event',
                  });
                }

                return lignes.map((l) => {
                  const contenu = (
                    <>
                      <dt className="text-slate-500 inline-flex items-start gap-2">
                        <i className={`bi ${l.icone} mt-0.5`} />
                        <span>{l.libelle}</span>
                      </dt>
                      <dd className="font-semibold text-slate-900 whitespace-nowrap text-right">{l.valeur}</dd>
                    </>
                  );
                  // Les lignes de visite et de dénonce ouvrent la fiche visite.
                  return l.visiteId ? (
                    <div
                      key={l.cle}
                      role="button"
                      tabIndex={0}
                      onClick={() => window.location.assign(`/visites/${l.visiteId}?retour=/properties/${property.id}/view`)}
                      onKeyDown={(ev) => { if (ev.key === 'Enter') window.location.assign(`/visites/${l.visiteId}?retour=/properties/${property.id}/view`); }}
                      data-testid={`view-history-visite-link-${l.visiteId}`}
                      className="py-3 flex justify-between gap-3 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors"
                    >
                      {contenu}
                    </div>
                  ) : (
                    <div key={l.cle} className="py-3 flex justify-between gap-3" data-testid={l.testid}>
                      {contenu}
                    </div>
                  );
                });
              })()}
            </dl>
          </article>
        </div>

        {/* ─────── SIDEBAR ─────── */}
        <aside className="space-y-4 lg:sticky lg:top-32 lg:self-start" data-testid="view-sidebar">
          {/* Résumé IA */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl text-white p-5" data-testid="view-ai-summary">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <i className="bi bi-stars" />Résumé IA
              </h3>
              <button
                type="button"
                onClick={regenerateAiSummary}
                disabled={generatingAiSummary}
                data-testid="view-ai-summary-regenerate-btn"
                className="text-xs bg-white/15 hover:bg-white/25 disabled:bg-white/5 px-2.5 py-1 rounded-lg flex items-center gap-1 transition"
                title="Régénérer le résumé avec l'IA"
              >
                {generatingAiSummary ? (
                  <><i className="bi bi-arrow-repeat animate-spin" /><span>IA…</span></>
                ) : (
                  <><i className="bi bi-arrow-clockwise" /><span>Régénérer</span></>
                )}
              </button>
            </div>
            <p className="text-xs leading-relaxed text-white/90 whitespace-pre-line">{resumeIA}</p>
            {(property.ai_summary_generated_at || aiSummaryOverride) && (
              <p className="text-[10px] text-white/60 mt-2 italic">
                <i className="bi bi-clock-history" /> Généré {aiSummaryOverride ? "à l'instant" : property.ai_summary_generated_at ? `le ${new Date(property.ai_summary_generated_at).toLocaleDateString('fr-FR')}` : ''}
              </p>
            )}
          </div>

          {/* Négociateur */}
          {negociateur && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Négociateur en charge</div>
              <div className="flex items-center gap-3">
                {negociateur.photo_url ? (
                  <img src={negociateur.photo_url} alt={negoFullName || 'Négociateur'} className="w-11 h-11 rounded-full object-cover" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm">
                    {negoInitials}
                  </div>
                )}
                <div>
                  <div className="font-bold text-slate-900">{negoFullName || '—'}</div>
                  {agence?.nom && <div className="text-xs text-slate-500">{agence.nom}</div>}
                </div>
              </div>
              {(negociateur.telephone || negociateur.email) && (
                <div className="mt-4 space-y-1.5 text-xs">
                  {negociateur.telephone && (
                    <a href={`tel:${negociateur.telephone}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition num">
                      <i className="bi bi-telephone-fill text-blue-500" />{formatPhoneFR(negociateur.telephone)}
                    </a>
                  )}
                  {negociateur.email && (
                    <a href={`mailto:${negociateur.email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition break-all">
                      <i className="bi bi-envelope-fill text-blue-500" />{negociateur.email}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Localisation — carte Leaflet/OSM (RGPD : secteur approximatif uniquement) */}
          {((ville && ville !== '—') || secteur || (canViewConfidential && confidentialLoc.lat)) && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="view-localisation">
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <i className="bi bi-geo-alt-fill text-rose-500" />Localisation
            </h3>
            <LocationMap
              ville={ville}
              secteur={secteur}
              exactLat={canViewConfidential ? confidentialLoc.lat : null}
              exactLon={canViewConfidential ? confidentialLoc.lon : null}
              approxLat={publicLoc.zone_lat}
              approxLon={publicLoc.zone_lon}
              isConfidential={!!(canViewConfidential && confidentialLoc.lat)}
            />
            <p className="text-[11px] text-slate-500 mt-3">
              <i className="bi bi-info-circle" /> {canViewConfidential && confidentialLoc.lat ? 'Adresse exacte (interne)' : 'Zone approximative (publique) — informations indicatives et non identifiables'}
            </p>
          </div>
          )}

          {/* Matching Acquéreurs */}
          <ContactMatchingWidget propertyId={property?.id} variant="default" />

          {/* Simulateur contextuel — piloté par l'opération. */}
          <ContextualSimulator
            operation={operation}
            informationsVente={vente}
            informationsBail={bail}
            informationsConfidentielles={confid}
            surface={property?.composition?.superficie_totale}
          />

          {/* Diffusion (LOT 3 — remplace « Actions rapides ») */}
          <DiffusionCard
            property={property}
            onDiffusionChange={(newDiffusion) => {
              // Sync local pour refléter instantanément le nouveau statut dans la carte.
              if (typeof onPropertyPatched === 'function') {
                onPropertyPatched({ ...property, diffusion: newDiffusion });
              }
            }}
          />
        </aside>
      </section>

      {/* ═════════ PHOTOS ═════════ */}
      {photos.length > 0 && (
        <section id="sec-photos" className="max-w-6xl mx-auto px-4 md:px-8 mt-12 scroll-mt-32" data-testid="view-section-photos">
          <SectionTitle>Photos du bien</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            {photos.map((p, i) => {
              const url = getMediaUrl(p);
              return (
                <button
                  key={p.id || i}
                  type="button"
                  onClick={() => { setGalleryIndex(i); setGalleryOpen(true); }}
                  className="aspect-square rounded-xl bg-cover bg-center hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ backgroundImage: `url('${url}')` }}
                  aria-label={p.name || `Photo ${i + 1}`}
                  data-testid={`view-photo-${i}`}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ═════════ DOCUMENTS INTERNES (repliable) ═════════ */}
      <section id="sec-documents-internes" className="max-w-6xl mx-auto px-4 md:px-8 mt-12 scroll-mt-32" data-testid="view-section-documents-internes">
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="view-documents">
          <DocumentsInternesPanel entityType="property" entityId={property.id} collapsible defaultOpen={false} />
        </div>
      </section>

      {/* ═════════ MÉDIAS — Vidéo (visite virtuelle temporairement masquée, flag SHOW_VISITE_VIRTUELLE) ═════════ */}
      {(() => {
        const SHOW_VISITE_VIRTUELLE = false; // Réactivable en une ligne (v1.1)
        const hasVideo = !!medias?.video_url;
        const hasVisite = SHOW_VISITE_VIRTUELLE && !!medias?.visite_virtuelle_url;
        if (!hasVideo && !hasVisite) return null;
        return (
          <section id="sec-medias" className="max-w-6xl mx-auto px-4 md:px-8 mt-12 scroll-mt-32" data-testid="view-section-medias">
            <SectionTitle>Médias additionnels</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              {hasVideo && (
                <a
                  href={medias.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-2xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-md transition flex items-center gap-4"
                  data-testid="view-link-video"
                >
                  <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600">
                    <i className="bi bi-play-btn-fill text-2xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">Vidéo de présentation</p>
                    <p className="text-xs text-slate-500 truncate">{medias.video_url}</p>
                  </div>
                  <i className="bi bi-box-arrow-up-right text-slate-400 group-hover:text-blue-600"></i>
                </a>
              )}
              {hasVisite && (
                <a
                  href={medias.visite_virtuelle_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-2xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-md transition flex items-center gap-4"
                  data-testid="view-link-visite-virtuelle"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                    <i className="bi bi-badge-3d-fill text-2xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">Visite virtuelle 3D</p>
                    <p className="text-xs text-slate-500 truncate">{medias.visite_virtuelle_url}</p>
                  </div>
                  <i className="bi bi-box-arrow-up-right text-slate-400 group-hover:text-blue-600"></i>
                </a>
              )}
            </div>
          </section>
        );
      })()}

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-4 md:px-8 mt-12 mb-8 text-center text-xs text-slate-500" data-testid="view-footer">
        {reference && (
          <p>
            Référence <span className="font-mono font-semibold">{reference}</span>
            {' '}· Édité le {property.updated_at ? new Date(property.updated_at).toLocaleDateString('fr-FR') : '—'}
          </p>
        )}
      </footer>

      {/* Galerie photos modale (lightbox simple) */}
      {galleryOpen && photos[galleryIndex] && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setGalleryOpen(false)}
          data-testid="view-photo-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setGalleryOpen(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <i className="bi bi-x-lg text-xl" />
          </button>
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setGalleryIndex(i => (i - 1 + photos.length) % photos.length); }}
                className="absolute left-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              >
                <i className="bi bi-chevron-left text-2xl" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setGalleryIndex(i => (i + 1) % photos.length); }}
                className="absolute right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              >
                <i className="bi bi-chevron-right text-2xl" />
              </button>
            </>
          )}
          <img
            src={getMediaUrl(photos[galleryIndex])}
            alt={photos[galleryIndex].name || ''}
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}

      {/* Modale — Historique des prix */}
      {showPriceHistory && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4"
          onClick={() => setShowPriceHistory(false)}
          data-testid="price-history-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <i className="bi bi-graph-up text-blue-500" />Historique des prix
              </h3>
              <button onClick={() => setShowPriceHistory(false)} data-testid="price-history-close" className="text-slate-400 hover:bg-slate-100 rounded-lg p-1.5">
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="p-5">
              {priceHistory.length === 0 ? (
                <div data-testid="price-history-empty">
                  <div className="flex items-center justify-between py-3 px-4 bg-blue-50 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-500">Prix actuel (point de départ)</p>
                      <p className="text-lg font-bold text-slate-900">{fmtPrice(currentRefPrice)}</p>
                    </div>
                    <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">Actuel</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">
                    <i className="bi bi-info-circle" /> Les changements de prix seront journalisés à partir de maintenant.
                  </p>
                </div>
              ) : (
                <ol className="relative border-l-2 border-slate-100 ml-2 space-y-4" data-testid="price-history-list">
                  {[...priceHistory].reverse().map((h, i) => {
                    const up = h.ancien_prix != null && h.prix > h.ancien_prix;
                    const down = h.ancien_prix != null && h.prix < h.ancien_prix;
                    return (
                      <li key={i} className="ml-4" data-testid="price-history-entry">
                        <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${i === 0 ? 'bg-blue-500' : 'bg-slate-300'}`} />
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-slate-900">{fmtPrice(h.prix)}</span>
                          {h.ancien_prix != null && (
                            <span className={`text-xs font-semibold flex items-center gap-1 ${up ? 'text-rose-600' : down ? 'text-emerald-600' : 'text-slate-400'}`}>
                              <i className={`bi ${up ? 'bi-arrow-up-right' : down ? 'bi-arrow-down-right' : 'bi-dash'}`} />
                              {fmtPrice(h.ancien_prix)}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400">{h.date ? new Date(h.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}</p>
                      </li>
                    );
                  })}
                  <li className="ml-4">
                    <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-slate-200" />
                    <p className="text-xs text-slate-400">Prix actuel : <span className="font-semibold text-slate-600">{fmtPrice(currentRefPrice)}</span></p>
                  </li>
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
      {showDiffusionModal && (
        <DiffusionManagerModal
          property={property}
          diffusion={property?.diffusion}
          onClose={() => setShowDiffusionModal(false)}
          onChange={(newDiffusion) => {
            if (typeof onPropertyPatched === 'function') {
              onPropertyPatched({ ...property, diffusion: newDiffusion });
            }
          }}
        />
      )}
    </div>
  );
};

export default PropertyViewContent;
